import React, { useMemo } from "react";

import { RadioGroup, Radio, FormControlLabel } from "@mui/material";

import DateInput from "components/DateInput";
import { InputGeneral } from "components/Input";

import {
  getRequiredCustomFieldErrors,
  IHearingCustomField,
} from "services/hearingCustomField";

import {
  CustomFieldsContainer,
  RadioBoxContainer,
  TextFieldsRow,
  TextFieldWrapper,
} from "./styles";

export interface ICustomFieldValue {
  id: number;
  value: string | boolean;
}

interface IHearingCustomFieldsProps {
  fields: IHearingCustomField[];
  showErrors?: boolean;
  values: Record<number, string | boolean>;
  onChange: (fieldId: number, value: string | boolean) => void;
  dateLimits?: Record<
    number,
    {
      minDate?: string | null;
      maxDate?: string | null;
    }
  >;
}

interface GroupedFields {
  container: string;
  fields: IHearingCustomField[];
  isRadioGroup: boolean;
}

export const HearingCustomFields: React.FC<IHearingCustomFieldsProps> = ({
  fields,
  values,
  onChange,
  dateLimits,
  showErrors = false,
}) => {
  const errors = showErrors ? getRequiredCustomFieldErrors(fields, values) : {};

  const groupedFields = useMemo(() => {
    const groups: Record<string, IHearingCustomField[]> = {};

    const sortedFields = [...fields].sort((a, b) => a.index - b.index);

    sortedFields.forEach((field) => {
      const container = field.container || "default";
      if (!groups[container]) {
        groups[container] = [];
      }
      groups[container].push(field);
    });

    const result: GroupedFields[] = Object.entries(groups).map(
      ([container, containerFields]) => ({
        container,
        fields: containerFields,
        isRadioGroup: containerFields.every(
          (f) => f.component === "RadioButton",
        ),
      }),
    );

    return result.sort((a, b) => {
      const minIndexA = Math.min(...a.fields.map((f) => f.index));
      const minIndexB = Math.min(...b.fields.map((f) => f.index));
      return minIndexA - minIndexB;
    });
  }, [fields]);

  const handleRadioChange = (
    containerFields: IHearingCustomField[],
    selectedFieldId: number,
  ) => {
    containerFields.forEach((field) => {
      onChange(field.id, field.id === selectedFieldId);
    });
  };

  const getSelectedRadioValue = (
    containerFields: IHearingCustomField[],
  ): string => {
    const selectedField = containerFields.find(
      (field) => values[field.id] === true,
    );
    return selectedField ? String(selectedField.id) : "";
  };

  const renderTextFields = (containerFields: IHearingCustomField[]) => {
    return (
      <TextFieldsRow>
        {containerFields.map((field) => {
          const isDateInput =
            field.component === "DateInput" ||
            field.type === "date" ||
            field.type === "datetime";
          const dateLimit = dateLimits?.[field.id];

          return (
            <TextFieldWrapper key={field.id} size={field.size}>
              {isDateInput ? (
                <DateInput
                  label={field.name}
                  value={String(values[field.id] ?? "")}
                  setValue={(value) => onChange(field.id, value ?? "")}
                  minDate={dateLimit?.minDate}
                  maxDate={dateLimit?.maxDate}
                  required={field.required}
                  error={Boolean(errors[field.id])}
                />
              ) : (
                <InputGeneral
                  label={field.name}
                  value={String(values[field.id] ?? "")}
                  onChange={(value: string) => onChange(field.id, value)}
                  maxLength={field.size}
                  error={Boolean(errors[field.id])}
                  startFocus={false}
                  required={field.required}
                />
              )}
            </TextFieldWrapper>
          );
        })}
      </TextFieldsRow>
    );
  };

  const renderRadioGroup = (containerFields: IHearingCustomField[]) => {
    const selectedValue = getSelectedRadioValue(containerFields);

    return (
      <RadioBoxContainer>
        <RadioGroup
          value={selectedValue}
          onChange={(e) =>
            handleRadioChange(containerFields, Number(e.target.value))
          }
          row
          sx={{
            "& .MuiFormControlLabel-root": { marginRight: "24px" },
            "& .MuiRadio-root": {
              color: "#666666",
              "&.Mui-checked": { color: "#1D9BF0" },
            },
          }}
        >
          {containerFields.map((field) => (
            <FormControlLabel
              key={field.id}
              value={String(field.id)}
              control={<Radio size="small" />}
              label={field.name}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#666666",
                },
              }}
            />
          ))}
        </RadioGroup>
      </RadioBoxContainer>
    );
  };

  if (fields.length === 0) {
    return null;
  }

  return (
    <CustomFieldsContainer>
      {groupedFields.map((group) => (
        <div key={group.container}>
          {group.isRadioGroup
            ? renderRadioGroup(group.fields)
            : renderTextFields(group.fields)}
        </div>
      ))}
    </CustomFieldsContainer>
  );
};
